import { AlertTriangle, Inbox, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn.js';

export function Spinner({ label = 'Chargement…', className }: { label?: string; className?: string }) {
  return (
    <div role="status" className={cn('flex items-center gap-2 text-sm text-ink-3', className)}>
      <Loader2 size={18} strokeWidth={1.75} className="animate-spin" aria-hidden />
      {label}
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
  icon,
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-line bg-surface px-6 py-10 text-center">
      <span className="text-ink-3" aria-hidden>
        {icon ?? <Inbox size={24} strokeWidth={1.75} />}
      </span>
      <p className="font-display text-[15px] font-bold text-ink">{title}</p>
      {children && <div className="max-w-[52ch] text-sm text-ink-2">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Une erreur est survenue',
  children,
}: {
  title?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-md border border-danger-100 bg-danger-50 px-4 py-3"
    >
      <AlertTriangle size={20} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger-700" aria-hidden />
      <div className="text-sm text-danger-700">
        <p className="font-semibold">{title}</p>
        {children && <div className="mt-0.5">{children}</div>}
      </div>
    </div>
  );
}

/** Bandeau d'information neutre. */
export function Notice({
  children,
  icon,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border border-line-soft bg-surface-2 px-4 py-3 text-[13.5px] text-ink-2',
        className,
      )}
    >
      {icon && <span className="mt-0.5 shrink-0 text-ink-3">{icon}</span>}
      <div className="min-w-0">{children}</div>
    </div>
  );
}
